import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { Helmet } from 'react-helmet'
import { Col, Container, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import {
  Button,
  useNotificationService,
  useErrorService,
} from '@abroad/components'
import { DateRangePicker } from '../components'
import { ClearFilters, PageHeader } from '../components/common'
import reportConstants from '../constants/reports'
import API from '../utils/API'

const REPORT_TYPE = 'scheduled-sessions'
const DATE_FORMAT = 'YYYY-MM-DD'
const NO_DATA_MESSAGE = 'No scheduled sessions match the selected filters.'
const CLIENT_COLUMN_KEYS = ['client', 'organization', 'networks', 'groups']
// Email is shown under the client name, so it has no column of its own
const HIDDEN_COLUMN_KEYS = ['clientEmail']
const COACH_COLUMN_KEYS = ['coach']
const DOWNLOAD_ERROR_CODES = [
  'report_job_not_found',
  'report_not_ready',
  'report_file_missing',
]

const { REPORT_JOB_STATUS, POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS } =
  reportConstants

const selectClassName =
  'account-daterange report-coach profile-select bg-white-shadow user-input form-control'

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const displayValue = (value) =>
  value === null || value === undefined || value === '' ? '-' : String(value)

// The client name opens the client's user page in a new tab, same as the accounts report
const renderClientName = (name, clientId) =>
  clientId ? (
    <Link
      to={`/admin/users/${clientId}`}
      target='_blank'
      className='scheduled-sessions-client-link'>
      {name}
    </Link>
  ) : (
    name
  )

// Same grouping as the Excel export: clients by name, then coach, date/time order kept
const groupRowsByClient = (rows) => {
  const clients = new Map()

  rows.forEach((row) => {
    const key = `${row.clientEmail}|${row.client}`

    if (!clients.has(key)) {
      clients.set(key, { first: row, rowCount: 0, coaches: new Map() })
    }

    const client = clients.get(key)
    if (!client.coaches.has(row.coach)) {
      client.coaches.set(row.coach, [])
    }
    client.coaches.get(row.coach).push(row)
    client.rowCount += 1
  })

  return [...clients.values()]
    .map(({ first, rowCount, coaches }) => ({
      first,
      rowCount,
      coaches: [...coaches.values()],
    }))
    .sort(
      (a, b) =>
        a.first.client.localeCompare(b.first.client) ||
        a.first.clientEmail.localeCompare(b.first.clientEmail),
    )
}

const ScheduledSessions = () => {
  const [coachList, setCoachList] = useState([])
  const [organizationList, setOrganizationList] = useState([])
  const [networkList, setNetworkList] = useState([])
  const [groupList, setGroupList] = useState([])
  const [groupsLoading, setGroupsLoading] = useState(false)
  const [coachId, setCoachId] = useState('')
  const [organization, setOrganization] = useState('')
  const [network, setNetwork] = useState('')
  const [groupId, setGroupId] = useState('')
  const [reportData, setReportData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  // Empty by default: the backend then uses today to today + 30 days
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  // Changing the key remounts the picker, which clears its input
  const [datePickerKey, setDatePickerKey] = useState(0)
  // Prevents double-clicks from starting parallel view + export jobs
  const requestInFlightRef = useRef(false)
  // Lets a slower group request finish without overwriting a newer one
  const groupRequestRef = useRef(0)
  const picker = useRef()
  const Error = useErrorService()
  const Notification = useNotificationService()

  const isBusy = reportLoading || exportLoading

  const isAnyFilterActive = Boolean(
    coachId || organization || network || groupId || startDate || endDate,
  )

  // Reports run only once at least one filter is set
  const isReportDisabled = isBusy || !isAnyFilterActive

  // Organization and network can't both be sent (one entity per request)
  const isOrganizationDisabled = !!network
  const isNetworkDisabled = !!organization

  const loadGroups = async (organizationName, networkName) => {
    const requestId = ++groupRequestRef.current

    try {
      setGroupsLoading(true)

      let groups = []

      if (organizationName || networkName) {
        const type = organizationName ? 'company' : 'network'
        const name = organizationName || networkName
        const { data } = await API.AdminAPI.entityService.getEntityGroups(
          type,
          encodeURIComponent(name),
        )

        groups = (data || [])
          .filter((group) => !group.isArchived)
          .map((group) => ({ id: group._id || group.id, name: group.name }))
      } else {
        const { data } = await API.AdminAPI.report.getEntityName('group')
        groups = data || []
      }

      if (requestId === groupRequestRef.current) {
        setGroupList(groups.sort((a, b) => a.name.localeCompare(b.name)))
      }
    } catch (error) {
      if (requestId === groupRequestRef.current) {
        setGroupList([])
      }
      Error.showError(error)
    } finally {
      if (requestId === groupRequestRef.current) {
        setGroupsLoading(false)
      }
    }
  }

  useEffect(() => {
    const getFilterOptions = async () => {
      try {
        const { data } = await API.AdminAPI.userList.getCoaches()
        setCoachList(data || [])
      } catch (error) {
        Error.showError(error)
      }
    }

    const getEntityOptions = async (entityType, setList) => {
      try {
        const { data } = await API.AdminAPI.report.getEntityName(entityType)
        setList(data || [])
      } catch (error) {
        Error.showError(error)
      }
    }

    getFilterOptions()
    getEntityOptions('organization', setOrganizationList)
    getEntityOptions('network', setNetworkList)
    loadGroups('', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOrganizationChange = (value) => {
    setOrganization(value)
    setGroupId('')
    setReportData(null)
    loadGroups(value, '')
  }

  const handleNetworkChange = (value) => {
    setNetwork(value)
    setGroupId('')
    setReportData(null)
    loadGroups('', value)
  }

  const handleClearFilters = () => {
    setCoachId('')
    setOrganization('')
    setNetwork('')
    setGroupId('')
    setStartDate(null)
    setEndDate(null)
    setDatePickerKey((key) => key + 1)
    setReportData(null)
    loadGroups('', '')
  }

  // A group is sent by id and takes priority; the organization or network then only narrows the group list
  const buildRequestBody = () => {
    const requestBody = {
      fields: {
        timezone: moment.tz.guess(),
      },
    }

    // Dates are sent as a pair; without them the backend uses today to today + 30 days
    if (startDate && endDate) {
      requestBody.fields.startDate = startDate.format(DATE_FORMAT)
      requestBody.fields.endDate = endDate.format(DATE_FORMAT)
    }

    if (coachId) {
      requestBody.fields.coachId = coachId
    }

    const selectedGroup = groupList.find((group) => group.id === groupId)

    if (selectedGroup) {
      requestBody.entityType = 'group'
      requestBody.id = selectedGroup.id
      requestBody.entityName = selectedGroup.name
    } else if (organization) {
      requestBody.entityType = 'organization'
      requestBody.entityName = organization
    } else if (network) {
      requestBody.entityType = 'network'
      requestBody.entityName = network
    }

    return requestBody
  }

  const handleRequestError = (error) => {
    if (DOWNLOAD_ERROR_CODES.includes(error?.code)) {
      Notification.showNotification(
        error.message || 'Unable to download report.',
        'danger',
      )
    } else {
      Error.showError(error)
    }
  }

  // Returns the ready job status, or null when the job failed (toast already shown)
  const pollReportJob = async (jobId) => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      const { data } = await API.AdminAPI.report.getExportStatus(jobId)

      if (data?.status === REPORT_JOB_STATUS.READY) {
        return data
      }

      if (
        data?.status === REPORT_JOB_STATUS.ERROR ||
        data?.status === REPORT_JOB_STATUS.FAILED
      ) {
        Notification.showNotification(
          data.error === 'NO_DATA'
            ? NO_DATA_MESSAGE
            : data.error === 'REPORT_TIMED_OUT'
            ? 'The report timed out. Please try again.'
            : 'Report generation failed. Please try again.',
          'danger',
        )
        return null
      }

      await sleep(POLL_INTERVAL_MS)
    }

    Notification.showNotification(
      'Report generation timed out. Please try again.',
      'danger',
    )
    return null
  }

  // Returns { jobId, status } or null when the job cannot be completed
  const runReportJob = async (isExport) => {
    const requestBody = buildRequestBody()

    const { data } = isExport
      ? await API.AdminAPI.report.exportReport(REPORT_TYPE, requestBody)
      : await API.AdminAPI.report.viewReport(REPORT_TYPE, requestBody)

    const jobId = data?.jobId

    if (!jobId) {
      Notification.showNotification(
        data?.message || 'Report job could not be started. Please try again.',
        'danger',
      )
      return null
    }

    const status = await pollReportJob(jobId)

    return status ? { jobId, status } : null
  }

  const handleViewReport = async () => {
    if (requestInFlightRef.current) {
      return
    }

    try {
      requestInFlightRef.current = true
      setReportLoading(true)
      setReportData(null)

      const job = await runReportJob(false)
      if (!job) {
        return
      }

      const { data } = await API.AdminAPI.report.downloadReport(job.jobId)
      setReportData(data)
    } catch (error) {
      handleRequestError(error)
    } finally {
      requestInFlightRef.current = false
      setReportLoading(false)
    }
  }

  const handleExportReport = async () => {
    if (requestInFlightRef.current) {
      return
    }

    try {
      requestInFlightRef.current = true
      setExportLoading(true)

      const job = await runReportJob(true)
      if (!job) {
        return
      }

      const { data: blob } = await API.AdminAPI.report.downloadReport(
        job.jobId,
        { asBlob: true },
      )

      if (blob?.size > 0) {
        const filename = `${job.status.fileName || 'Scheduled Sessions'}.xlsx`
          .replace(/[/\\?%*:|"<>]/g, '')
          .trim()

        const downloadLink = document.createElement('a')
        const url = window.URL.createObjectURL(blob)

        downloadLink.href = url
        downloadLink.download = filename
        downloadLink.style.display = 'none'

        document.body.appendChild(downloadLink)
        downloadLink.click()

        document.body.removeChild(downloadLink)
        window.URL.revokeObjectURL(url)

        Notification.showNotification(
          'Report exported successfully.',
          'success',
        )
      }
    } catch (error) {
      handleRequestError(error)
    } finally {
      requestInFlightRef.current = false
      setExportLoading(false)
    }
  }

  const renderReportRows = (columnKeys, clientBlocks) => {
    return clientBlocks.flatMap((block, blockIndex) => {
      let clientRowIndex = 0

      return block.coaches.flatMap((coachRows) =>
        coachRows.map((row, coachRowIndex) => {
          const isFirstClientRow = clientRowIndex === 0
          const cells = columnKeys.map((key) => {
            if (key === 'client') {
              return clientRowIndex === 0 ? (
                <td key={key} rowSpan={block.rowCount}>
                  {renderClientName(
                    displayValue(block.first.client),
                    block.first.clientId,
                  )}
                  <br />
                  {displayValue(block.first.clientEmail)}
                </td>
              ) : null
            }

            if (CLIENT_COLUMN_KEYS.includes(key)) {
              return clientRowIndex === 0 ? (
                <td key={key} rowSpan={block.rowCount}>
                  {displayValue(block.first[key])}
                </td>
              ) : null
            }

            if (COACH_COLUMN_KEYS.includes(key)) {
              return coachRowIndex === 0 ? (
                <td key={key} rowSpan={coachRows.length}>
                  {displayValue(row[key])}
                </td>
              ) : null
            }

            return <td key={key}>{displayValue(row[key])}</td>
          })

          clientRowIndex += 1

          return (
            <tr key={`${blockIndex}-${clientRowIndex}`}>
              {isFirstClientRow ? (
                <td rowSpan={block.rowCount}>{blockIndex + 1}</td>
              ) : null}
              {cells}
            </tr>
          )
        }),
      )
    })
  }

  const renderReport = () => {
    const rows = reportData?.data || []

    if (rows.length === 0) {
      return (
        <div className='text-center py-4'>
          <h3 className='font-montserrat'>No Report Data Found.</h3>
        </div>
      )
    }

    const columnLabels = reportData.keyValueDataForColumnName || {}
    const columnKeys = Object.keys(columnLabels).filter(
      (key) => !HIDDEN_COLUMN_KEYS.includes(key),
    )

    return (
      <Container
        className='account-report custom-offset-lg-1 custom-col-lg-12 px-0'
        fluid>
        <Row className='m-0 d-lg-block'>
          <div className='col-xs-12'>
            <Table hover responsive className='custom-table'>
              <thead>
                <tr>
                  <th>#</th>
                  {columnKeys.map((key) => (
                    <th key={key}>{columnLabels[key]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderReportRows(columnKeys, groupRowsByClient(rows))}
              </tbody>
            </Table>
          </div>
        </Row>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>{'Scheduled Sessions | Abroad'}</title>
        <meta name='title' content={'Scheduled Sessions | Abroad'}></meta>
        <meta
          property='og:title'
          content={'Scheduled Sessions | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Scheduled Sessions | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/scheduled-sessions`}></link>
      </Helmet>
      <div>
        <PageHeader
          titleText='Scheduled Sessions'
          colClasses='px-0'
          titleClasses='sentence-case-text'
        />
        <section className='account pt-5'>
          <Container fluid className='custom-offset-lg-1 custom-col-lg-12 px-0'>
            <Row className='custom-report-container'>
              <Col xs={4} className='mb-2'>
                <select
                  name='coach'
                  id='coach'
                  className={selectClassName}
                  value={coachId}
                  onChange={(e) => {
                    setCoachId(e.target.value)
                    setReportData(null)
                  }}>
                  <option value=''>All Coaches</option>
                  {coachList.map((coach) => (
                    <option
                      value={coach._id || coach.id}
                      key={coach._id || coach.id}>
                      {coach.fullName}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={4} className='mb-2'>
                <select
                  name='organization'
                  id='organization'
                  className={classNames(selectClassName, {
                    'disabled-field': isOrganizationDisabled,
                  })}
                  value={organization}
                  onChange={(e) => handleOrganizationChange(e.target.value)}
                  disabled={isOrganizationDisabled}>
                  <option value=''>All Organizations</option>
                  {organizationList.map((entity) => (
                    <option value={entity.name} key={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={4} className='mb-2'>
                <select
                  name='network'
                  id='network'
                  className={classNames(selectClassName, {
                    'disabled-field': isNetworkDisabled,
                  })}
                  value={network}
                  onChange={(e) => handleNetworkChange(e.target.value)}
                  disabled={isNetworkDisabled}>
                  <option value=''>All Networks</option>
                  {networkList.map((entity) => (
                    <option value={entity.name} key={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={4} className='mb-2'>
                <select
                  name='group'
                  id='group'
                  className={classNames(selectClassName, {
                    'disabled-field': groupsLoading,
                  })}
                  value={groupId}
                  onChange={(e) => {
                    setGroupId(e.target.value)
                    setReportData(null)
                  }}
                  disabled={groupsLoading}>
                  <option value=''>
                    {groupsLoading ? 'Loading Groups...' : 'All Groups'}
                  </option>
                  {groupList.map((group) => (
                    <option value={group.id} key={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={4} className='mb-2'>
                <DateRangePicker
                  key={datePickerKey}
                  initialSettings={{
                    opens: 'center',
                    // The shared picker caps dates at today; sessions can be past or future
                    maxDate: null,
                    linkedCalendars: false,
                  }}
                  showDropdowns
                  onShow={(event, datePicker) => {
                    datePicker.container.addClass('account-daterangepicker')
                  }}
                  onApply={(start, end) => {
                    setStartDate(start)
                    setEndDate(end)
                    setReportData(null)
                  }}
                  className='account-daterange w-100'
                  ref={picker}
                />
              </Col>
            </Row>
            {isAnyFilterActive && (
              <ClearFilters
                wrapperClasses='text-right'
                onClick={handleClearFilters}
                disabled={isBusy}
              />
            )}
            <div className='margin-auto text-center'>
              <Button
                type='button'
                variant='gray'
                size='md'
                className='line-height-normal text-uppercase border-radius-3'
                disabled={isReportDisabled}
                onClick={handleViewReport}>
                {reportLoading ? 'Loading...' : 'View Report'}
              </Button>
              <Button
                type='button'
                variant='gray'
                id='generate-report'
                size='md'
                className='line-height-normal text-uppercase border-radius-3'
                style={{ marginLeft: '10px' }}
                disabled={isReportDisabled}
                onClick={handleExportReport}>
                {exportLoading ? 'Exporting...' : 'Export Report'}
              </Button>
            </div>
          </Container>

          {reportLoading && (
            <div className='text-center py-4'>
              <Spinner animation='border' className='text-saffron-700' />
            </div>
          )}

          {!reportLoading && reportData && (
            <div className='mt-4'>{renderReport()}</div>
          )}
        </section>
      </div>
    </>
  )
}

export default ScheduledSessions
